"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Card, CardBody, CardHeader, Nav, NavItem, NavLink, TabContent, TabPane, Badge } from "reactstrap"
import { FaArrowLeft, FaCalendar, FaMapMarkerAlt, FaLayerGroup, FaDollarSign, FaUsers } from "react-icons/fa"
import { Btn } from "../../../AbstractElements"
import ExhibitionService from "../../../Service/ExhibitionService"
import type { Exhibition, ExhibitionStandCategory, ExhibitionPricing, Participation } from "./types"
import StandCategoriesTab from "./StandCategoriesTab"
import ExhibitionPricingTab from "./ExhibitionPricingTab"
import ParticipationsTab from "./ParticipationsTab"
import { toast } from "react-toastify"

interface Props {
  onAddStandCategory: () => void
  onEditStandCategory: (item: ExhibitionStandCategory) => void
  onAddPricing: () => void
  onEditPricing: (item: ExhibitionPricing) => void
  onAddParticipation: () => void
  onEditParticipation: (item: Participation) => void
  refreshTrigger: boolean
  setExhibitionId: React.Dispatch<React.SetStateAction<number>> // ✅ Add this line
}

const ExhibitionRelatedList: React.FC<Props> = ({
  onAddStandCategory,
  onEditStandCategory,
  onAddPricing,
  onEditPricing,
  onAddParticipation,
  onEditParticipation,
  refreshTrigger,
  setExhibitionId,
}) => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [exhibition, setExhibition] = useState<Exhibition | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState("1")

  // Fetch exhibition details using getById
  useEffect(() => {
    const fetchExhibition = async () => {
      if (!id) {
        toast.error("Exhibition ID is required")
        navigate(`${process.env.PUBLIC_URL}/exhibition`)
        return
      }

      try {
        setLoading(true)
        const exhibitionData = await ExhibitionService.getById(Number(id))
        setExhibition(exhibitionData)
        setExhibitionId(exhibitionData.id)
      } catch (error) {
        console.error("Failed to fetch exhibition:", error)
        toast.error("Failed to load exhibition details")
        navigate(`${process.env.PUBLIC_URL}/exhibition`)
      } finally {
        setLoading(false)
      }
    }

    fetchExhibition()
  }, [id, navigate])

  const handleBack = () => {
    navigate(`${process.env.PUBLIC_URL}/exhibition`)
  }

  const toggle = (tab: string) => {
    if (activeTab !== tab) setActiveTab(tab)
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="spinner-border" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    )
  }

  if (!exhibition) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="text-center">
          <h4>Exhibition not found</h4>
          <button className="btn btn-primary mt-3" onClick={handleBack}>
            Back to Exhibitions
          </button>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Header Section - Exhibition Details from getById */}
      <Card className="mb-4">
        <CardHeader>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <Btn color="secondary" size="sm" onClick={handleBack} className="me-3">
                <FaArrowLeft size={12} />
              </Btn>
              <div>
                <h4 className="mb-1">
                  {exhibition.title} || {exhibition.exhibition_type_name}
                </h4>
                <h4 className="mb-1">
                  {exhibition.phone_number} || {exhibition.email}
                </h4>
                <div className="d-flex align-items-center gap-3 text-muted">
                  <span className="d-flex align-items-center">
                    <FaMapMarkerAlt className="me-1" size={12} />
                    {exhibition.venue}
                  </span>
                  <span className="d-flex align-items-center">
                    <FaCalendar className="me-1" size={12} />
                    {exhibition.start_date} to {exhibition.end_date}
                  </span>
                </div>
              </div>

              
            </div>
            <Badge color={exhibition.status ? "success" : "secondary"} pill>
              {exhibition.status ? "Active" : "Inactive"}
            </Badge>
          </div>
        </CardHeader>
        {exhibition.description && (
          <CardBody>
            <p className="mb-0">{exhibition.description}</p>
          </CardBody>
        )}
      </Card>

      {/* Enhanced Tabs Section */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="bg-white border-0 pb-0">
          <Nav className="nav-tabs border-0">
            <NavItem>
              <NavLink
                className={`nav-link border-0 px-4 py-3 ${activeTab === "1" ? "active text-primary fw-bold" : "text-muted"}`}
                onClick={() => toggle("1")}
                style={{
                  cursor: "pointer",
                  borderBottom: activeTab === "1" ? "3px solid #0d6efd" : "3px solid transparent",
                }}
              >
                <FaLayerGroup className={`me-2 ${activeTab === "1" ? "text-primary" : "text-muted"}`} />
                Stand Categories
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={`nav-link border-0 px-4 py-3 ${activeTab === "2" ? "active text-primary fw-bold" : "text-muted"}`}
                onClick={() => toggle("2")}
                style={{
                  cursor: "pointer",
                  borderBottom: activeTab === "2" ? "3px solid #0d6efd" : "3px solid transparent",
                }}
              >
                <FaDollarSign className={`me-2 ${activeTab === "2" ? "text-primary" : "text-muted"}`} />
                Exhibition Pricing
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={`nav-link border-0 px-4 py-3 ${activeTab === "3" ? "active text-primary fw-bold" : "text-muted"}`}
                onClick={() => toggle("3")}
                style={{
                  cursor: "pointer",
                  borderBottom: activeTab === "3" ? "3px solid #0d6efd" : "3px solid transparent",
                }}
              >
                <FaUsers className={`me-2 ${activeTab === "3" ? "text-primary" : "text-muted"}`} />
                Participations
              </NavLink>
            </NavItem>
          </Nav>
        </CardHeader>
        <CardBody className="pt-4">
          <TabContent activeTab={activeTab}>
            <TabPane tabId="1">
              <StandCategoriesTab
                exhibitionId={exhibition.id}
                onAdd={onAddStandCategory}
                onEdit={onEditStandCategory}
                refresh={refreshTrigger}
              />
            </TabPane>
            <TabPane tabId="2">
              <ExhibitionPricingTab
                exhibitionId={exhibition.id}
                onAdd={onAddPricing}
                onEdit={onEditPricing}
                refresh={refreshTrigger}
              />
            </TabPane>
            <TabPane tabId="3">
              <ParticipationsTab
                exhibitionId={exhibition.id}
                onAdd={onAddParticipation}
                onEdit={onEditParticipation}
                refresh={refreshTrigger}
              />
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>
    </div>
  )
}

export default ExhibitionRelatedList
